<template>
  <div class="wfp" data-test="wf-picker">
    <Multiselect ref="ms" v-bind="msProps" v-on="$listeners" @open="onOpen">
      <template v-for="name in forwardedSlots()" v-slot:[name]="slotProps">
        <slot :name="name" v-bind="slotProps" />
      </template>

      <template v-if="useOptionSlot()" #option="{ option }">
        <span v-if="option && option.isTag" class="wfp__add" data-test="wf-picker-add">
          <WfIcon name="plus" :size="14" />
          <span>Add “<b>{{ option.label }}</b>” as a new tag</span>
        </span>
        <span v-else-if="procCodes" class="wfp__opt">
          <b class="wfp__code">{{ codeOf(option) }}</b>
          <span>{{ descOf(option) }}</span>
        </span>
        <span v-else>{{ plainLabel(option) }}</span>
      </template>

      <template v-if="useTagSlot()" #tag="{ option, remove }">
        <span :key="tagKey(option)" class="multiselect__tag">
          <span class="wfp__tag-text">
            <b class="wfp__code">{{ codeOf(option) }}</b>
            <span v-if="descOf(option)">{{ descOf(option) }}</span>
          </span>
          <i
            tabindex="1"
            role="button"
            aria-label="Remove"
            class="multiselect__tag-icon"
            @keypress.enter.prevent="remove(option)"
            @mousedown.prevent="remove(option)"
          ></i>
        </span>
      </template>

      <template v-if="!hasSlot('noResult')" #noResult>Nothing matches your search.</template>
      <template v-if="!hasSlot('noOptions')" #noOptions>Nothing to choose from yet.</template>
    </Multiselect>
  </div>
</template>

<script>
/**
 * The ONE dropdown of the redesigned block settings (design build note: "Fields are vue-multiselect
 * restyled in scoped SCSS: tags and options wrap, and the list matches the field's width").
 *
 * A thin wrapper: every attribute is passed to vue-multiselect (options, label, track-by, multiple,
 * taggable, close-on-select, v-model...) and every listener (input, tag, open...) and every slot are
 * forwarded unchanged, so a panel binds a picker exactly as the classic builder binds its <multiselect>.
 * What it adds: ONE restyle (42px, 10px radius, teal chips that wrap, options that wrap, a list exactly
 * the field's width that opens over the cards below, `max-width: none`), a 280px list height, the list
 * opening downward (the sheet body keeps 240px of room under the last field), and the list scrolled into
 * view when it opens.
 *
 * `proc-codes` draws a procedure code the design's way: the code in the mono face, then its description.
 * A taggable picker draws its "new tag" row as "Add "x" as a new tag".
 *
 * ⛔ The global stylesheet shrinks every .multiselect to 12px and the Settings page caps it at 500px;
 * both are restated below (scoped, via ::v-deep).
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import WfIcon from '../WfIcon.vue';

export default {
  name: 'WfPicker',
  components: { Multiselect, WfIcon },
  inheritAttrs: false,
  props: {
    /** The options are procedure codes ({ value, text: 'D0150 - description' }). */
    procCodes: { type: Boolean, default: false },
  },
  computed: {
    msProps() {
      // kebab-case keys on purpose: the parent writes its attributes that way, and an override must hit
      // the same key as the default.
      return Object.assign({ 'max-height': 280, 'open-direction': 'bottom' }, this.$attrs);
    },
  },
  methods: {
    forwardedSlots() {
      return Object.keys(this.$scopedSlots);
    },
    hasSlot(name) {
      return Object.prototype.hasOwnProperty.call(this.$scopedSlots, name);
    },
    useOptionSlot() {
      return (this.procCodes || this.$attrs.taggable === true || this.$attrs.taggable === '') && !this.hasSlot('option');
    },
    useTagSlot() {
      return this.procCodes && !this.hasSlot('tag');
    },
    textOf(option) {
      if (option === null || option === undefined) return '';
      if (typeof option !== 'object') return String(option);
      return option.text === undefined || option.text === null ? '' : String(option.text);
    },
    codeOf(option) {
      const text = this.textOf(option);
      const at = text.indexOf(' - ');
      return at === -1 ? text : text.slice(0, at);
    },
    descOf(option) {
      const text = this.textOf(option);
      const at = text.indexOf(' - ');
      return at === -1 ? '' : text.slice(at + 3);
    },
    tagKey(option) {
      return option && typeof option === 'object' && option.value !== undefined ? String(option.value) : this.textOf(option);
    },
    plainLabel(option) {
      if (option === null || option === undefined) return '';
      if (typeof option !== 'object') return String(option);
      const key = this.$attrs.label;
      return key && option[key] !== undefined && option[key] !== null ? String(option[key]) : '';
    },
    onOpen() {
      // The list is absolutely positioned inside the sheet's one scroller: bring it into view without
      // moving anything else.
      this.$nextTick(() => {
        const list = this.$el && this.$el.querySelector ? this.$el.querySelector('.multiselect__content-wrapper') : null;
        if (list && typeof list.scrollIntoView === 'function') list.scrollIntoView({ block: 'nearest' });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.wfp {
  position: relative;
  width: 100%;
  min-width: 0;
  font-family: inherit;

  // ---- the field -------------------------------------------------------------------------------
  ::v-deep .multiselect {
    width: 100%;
    max-width: none;
    min-height: 42px;
    font-size: 14px;
    color: $fu-text;
  }

  // Above the cards that follow it (the list itself is z 10050 from the global stylesheet).
  ::v-deep .multiselect--active { z-index: 60; }

  // ⛔ The !important flags on the field and its text are deliberate and contained (everything here sits under
  // the scoped .wfp[data-v] ancestor, so they reach nothing but this picker). An UNSCOPED stylesheet that is
  // loaded app-wide (src/pages/Settings/Settings.scss) forces, on EVERY .multiselect and with !important,
  //   .multiselect__tags { padding: 8px 40px 0 8px; border: 1px solid #e0e0e0; border-radius: 8px; min-height: 42px; background: #fff }
  //   .multiselect__input / __single { font-size: 0.95rem (13.3px); background: transparent }
  //   .multiselect__content-wrapper { border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 8px 8px }
  // Without these flags the field measured the wrong corners and padding and 13.3px text, which is under the
  // 16px an iPad needs (iOS zooms the page on focus and never zooms back; Cypress, 2026-10-10).
  ::v-deep .multiselect__tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-height: 42px !important;
    padding: 6px 38px 6px 6px !important;
    border: 1px solid $fu-neutral-300 !important;
    border-radius: 10px !important;
    background: $fu-surface !important;
    font-size: 14px;
    line-height: 1.4;
  }

  ::v-deep .multiselect--active .multiselect__tags {
    border-color: $fu-brand-400 !important;
    border-radius: 10px !important;
    box-shadow: $fu-ring;
  }

  // The chips and the search box share one wrapping row.
  ::v-deep .multiselect__tags-wrap { display: contents; }

  // ---- chips: teal, and they WRAP (never cut) ----------------------------------------------------
  ::v-deep .multiselect__tag {
    display: inline-flex;
    align-items: flex-start;
    max-width: 100%;
    margin: 0;
    padding: 4px 26px 4px 9px;
    border-radius: 7px;
    background: $fu-brand-50;
    color: $fu-brand-800;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: anywhere;
  }

  ::v-deep .multiselect__tag-icon {
    top: 3px;
    bottom: auto;
    right: 2px;
    width: 22px;
    height: 22px;
    margin: 0;
    border-radius: 6px;
    line-height: 22px;

    &::after { color: $fu-brand-700; font-size: 15px; }
    &:hover,
    &:focus { background: $fu-brand-100; }
    &:hover::after,
    &:focus::after { color: $fu-brand-800; }
  }

  .wfp__tag-text { min-width: 0; overflow-wrap: anywhere; }

  .wfp__code {
    margin-right: 6px;
    font-family: $pp-font-mono;
    font-size: 12px;
    font-weight: 700;
  }

  // ---- the text the field shows ------------------------------------------------------------------
  ::v-deep .multiselect__single {
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    margin: 0;
    padding: 0 4px;
    background: transparent !important;
    font-size: 14px !important;
    line-height: 1.4;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  ::v-deep .multiselect__placeholder {
    margin: 0;
    padding: 0 4px;
    color: $fu-neutral-400;
    // The same size as a value, so "Select wikis..." does not read smaller than "Immediately" next to it.
    font-size: 14px !important;
    line-height: 1.4;
  }

  ::v-deep .multiselect__input {
    flex: 1 1 140px;
    min-width: 120px;
    width: auto;
    min-height: 28px;
    height: 28px;
    margin: 0;
    padding: 0 4px;
    border: 0;
    background: transparent !important;
    font-size: 14px !important;
    line-height: 28px;
    color: $fu-text;

    &::placeholder { color: $fu-neutral-400; }
    &:focus { outline: none; }
  }

  // Closed, the vendor hides the search box (inline width 0, absolutely positioned) and the placeholder span
  // speaks for it. The min-width above would bring it back at 120px, with its OWN placeholder printed over the
  // span's (seen as doubled "Select ..." text in a real browser), so a closed picker takes the width away again.
  ::v-deep .multiselect:not(.multiselect--active) .multiselect__input {
    flex: 0 0 0;
    min-width: 0;
    width: 0 !important;
    height: 0;
    padding: 0;
    opacity: 0;
  }

  // The caret: pinned to the first line of the field.
  ::v-deep .multiselect__select {
    top: 0;
    right: 0;
    bottom: auto;
    width: 38px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      position: static;
      margin: 0;
      top: auto;
      right: auto;
      border-top-color: $fu-neutral-500;
    }
  }

  // ---- the list: exactly the field's width, over what is below ----------------------------------
  ::v-deep .multiselect__content-wrapper {
    top: calc(100% + 6px);
    left: 0;
    width: 100%;
    max-width: none;
    padding: 6px;
    border: 1px solid $fu-border !important;
    border-radius: 12px !important;
    background: $fu-surface;
    box-shadow: $fu-shadow-lg;
  }

  ::v-deep .multiselect--above .multiselect__content-wrapper {
    top: auto;
    bottom: calc(100% + 6px);
    border: 1px solid $fu-border !important;
    border-radius: 12px !important;
  }

  ::v-deep .multiselect__content {
    display: block;
    width: 100%;
    min-width: 0;
  }

  ::v-deep .multiselect__option {
    min-height: 0;
    padding: 9px 10px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.4;
    white-space: normal;
    overflow-wrap: anywhere;
    color: $fu-text;
  }

  // The vendor draws a green block from the (empty) data-select label; a selected row gets a check.
  ::v-deep .multiselect__option::after { display: none; }

  ::v-deep .multiselect__option--highlight,
  ::v-deep .multiselect__option--highlight::after {
    background: $fu-neutral-50;
    color: $fu-text;
    outline: none;
  }

  ::v-deep .multiselect__option--selected {
    padding-right: 34px;
    background: $fu-brand-50;
    color: $fu-text;
    font-weight: 700;
  }

  ::v-deep .multiselect__option--selected.multiselect__option--highlight { background: $fu-brand-100; color: $fu-text; }

  ::v-deep .multiselect__option--selected::after,
  ::v-deep .multiselect__option--selected.multiselect__option--highlight::after {
    content: '';
    display: block;
    top: 50%;
    right: 14px;
    width: 5px;
    height: 10px;
    margin-top: -7px;
    padding: 0;
    border: solid $fu-brand-700;
    border-width: 0 2px 2px 0;
    background: transparent;
    transform: rotate(45deg);
  }

  .wfp__opt { display: block; }

  .wfp__opt .wfp__code { margin-right: 8px; font-size: 12.5px; }

  .wfp__add {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $fu-brand-800;
  }

  // ---- iOS: a text box under 16px zooms the page on focus and never zooms back -------------------
  @include wf-touch {
    ::v-deep .multiselect__input,
    ::v-deep .multiselect__single,
    ::v-deep .multiselect__placeholder { font-size: 16px !important; }

    ::v-deep .multiselect__option { padding-top: 12px; padding-bottom: 12px; }
  }
}
</style>
